import { useState } from "react";
import { Modal, Select } from "antd";
import styles from "./index.module.scss";

const RING_SIZES = [
  { uk: "A", us: "0.5", eu: "41", diameter: "10.41", circ: "32.7" },
  { uk: "B", us: "1", eu: "42", diameter: "10.66", circ: "33.5" },
  { uk: "C", us: "1.5", eu: "43", diameter: "10.92", circ: "34.3" },
  { uk: "D", us: "2", eu: "44", diameter: "11.18", circ: "35.2" },
  { uk: "E", us: "2.5", eu: "45", diameter: "11.43", circ: "35.9" },
  { uk: "F", us: "3", eu: "46", diameter: "11.69", circ: "36.8" },
  { uk: "G", us: "3.5", eu: "47", diameter: "11.94", circ: "37.6" },
  { uk: "H", us: "4", eu: "48", diameter: "12.20", circ: "38.4" },
  { uk: "I", us: "4.5", eu: "49", diameter: "12.45", circ: "39.1" },
  { uk: "J", us: "5", eu: "50", diameter: "12.71", circ: "40.0" },
  { uk: "K", us: "5.5", eu: "51", diameter: "12.96", circ: "40.8" },
  { uk: "L", us: "6", eu: "52", diameter: "13.22", circ: "41.6" },
  { uk: "M", us: "6.5", eu: "53", diameter: "13.47", circ: "42.4" },
  { uk: "N", us: "7", eu: "54", diameter: "13.73", circ: "43.2" },
  { uk: "O", us: "7.5", eu: "55", diameter: "13.98", circ: "44.0" },
  { uk: "P", us: "8", eu: "56", diameter: "14.24", circ: "44.8" },
  { uk: "Q", us: "8.5", eu: "57", diameter: "14.49", circ: "45.6" },
  { uk: "R", us: "9", eu: "58", diameter: "14.75", circ: "46.5" },
  { uk: "S", us: "9.5", eu: "59", diameter: "15.00", circ: "47.2" },
  { uk: "T", us: "10", eu: "60", diameter: "15.26", circ: "48.1" },
  { uk: "U", us: "10.5", eu: "61", diameter: "15.51", circ: "48.8" },
  { uk: "V", us: "11", eu: "62", diameter: "15.77", circ: "49.7" },
  { uk: "W", us: "11.5", eu: "63", diameter: "16.02", circ: "50.5" },
  { uk: "X", us: "12", eu: "64", diameter: "16.28", circ: "51.3" },
  { uk: "Y", us: "12.5", eu: "65", diameter: "16.53", circ: "52.1" },
  { uk: "Z", us: "13", eu: "66", diameter: "16.79", circ: "52.9" },
  { uk: "Z+1", us: "13.5", eu: "67", diameter: "17.04", circ: "53.7" },
  { uk: "Z+2", us: "14", eu: "68", diameter: "17.30", circ: "54.5" },
  { uk: "Z+3", us: "14.5", eu: "69", diameter: "17.55", circ: "55.3" },
  { uk: "Z+4", us: "15", eu: "70", diameter: "17.81", circ: "56.2" },
  { uk: "Z+5", us: "15.5", eu: "71", diameter: "18.06", circ: "56.9" },
  { uk: "Z+6", us: "16", eu: "72", diameter: "18.32", circ: "57.8" },
];

function RingSizeChartModal({ open, onClose }) {
  const [ringSizeInput, setRingSizeInput] = useState("");
  const [ringSizeSystem, setRingSizeSystem] = useState("uk");

  return (
    <Modal
      open={open}
      onCancel={() => { onClose(); setRingSizeInput(""); }}
      footer={null}
      width={720}
      centered
      className={styles.ringSizeModal}
      title={null}
    >
      <div className={styles.ringSizeModalHeader}>
        <h3>Ring Size Conversion Chart</h3>
        <p>Our products use UK sizing. Use this chart to convert from other systems.</p>
      </div>

      {/* Interactive Converter */}
      <div className={styles.ringSizeConverter}>
        <div className={styles.converterRow}>
          <Select
            value={ringSizeSystem}
            onChange={(val) => { setRingSizeSystem(val); setRingSizeInput(""); }}
            className={styles.converterSelect}
            getPopupContainer={(trigger) => trigger.parentElement}
            popupMatchSelectWidth={false}
            options={[
              { value: "uk", label: "UK" },
              { value: "us", label: "US" },
              { value: "eu", label: "EU" },
              { value: "diameter", label: "Diameter (mm)" },
              { value: "circ", label: "Circumference (mm)" },
            ]}
          />
          <Select
            value={ringSizeInput || undefined}
            onChange={(val) => setRingSizeInput(val)}
            placeholder="Select a size"
            className={styles.converterInput}
            showSearch
            getPopupContainer={(trigger) => trigger.parentElement}
            popupMatchSelectWidth={false}
            virtual={false}
            options={RING_SIZES.map((r) => ({
              value: r[ringSizeSystem],
              label: ringSizeSystem === "uk" ? `UK ${r[ringSizeSystem]}` :
                     ringSizeSystem === "us" ? `US ${r[ringSizeSystem]}` :
                     ringSizeSystem === "eu" ? `EU ${r[ringSizeSystem]}` :
                     `${r[ringSizeSystem]} mm`,
            }))}
          />
        </div>
        {ringSizeInput && (() => {
          const match = RING_SIZES.find((r) => r[ringSizeSystem] === ringSizeInput);
          if (!match) return null;
          return (
            <div className={styles.converterResult}>
              <div className={styles.converterResultItem}>
                <span className={styles.converterResultLabel}>UK</span>
                <span className={styles.converterResultValue}>{match.uk}</span>
              </div>
              <div className={styles.converterResultItem}>
                <span className={styles.converterResultLabel}>US</span>
                <span className={styles.converterResultValue}>{match.us}</span>
              </div>
              <div className={styles.converterResultItem}>
                <span className={styles.converterResultLabel}>EU</span>
                <span className={styles.converterResultValue}>{match.eu}</span>
              </div>
              <div className={styles.converterResultItem}>
                <span className={styles.converterResultLabel}>Diameter</span>
                <span className={styles.converterResultValue}>{match.diameter}mm</span>
              </div>
              <div className={styles.converterResultItem}>
                <span className={styles.converterResultLabel}>Circumference</span>
                <span className={styles.converterResultValue}>{match.circ}mm</span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Full Table */}
      <div className={styles.ringSizeTableWrap}>
        <table className={styles.ringSizeTable}>
          <thead>
            <tr>
              <th>UK</th>
              <th>US</th>
              <th>EU</th>
              <th>Diameter (mm)</th>
              <th>Circumference (mm)</th>
            </tr>
          </thead>
          <tbody>
            {RING_SIZES.map((r, i) => (
              <tr
                key={i}
                className={ringSizeInput && r[ringSizeSystem] === ringSizeInput ? styles.ringSizeHighlight : ""}
              >
                <td>{r.uk}</td>
                <td>{r.us}</td>
                <td>{r.eu}</td>
                <td>{r.diameter}</td>
                <td>{r.circ}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export default RingSizeChartModal;
